import React, { useState, useEffect } from 'react';
import { Tabs, Card, Input, Button, Table, Modal, Form, Select, Switch, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import { api } from '../../../utils/apiClient';
import { useUser } from '../../../contexts/UserContext';
import './Settings.css';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

const Settings = () => {
  const { privileges } = useUser();
  const [activeTab, setActiveTab] = useState('prompts');
  const [loading, setLoading] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({});
  const [outputFormats, setOutputFormats] = useState([]);
  const [industryPresets, setIndustryPresets] = useState({});
  const [auditHistory, setAuditHistory] = useState([]);
  const [selectedAuditKey, setSelectedAuditKey] = useState('all');

  // Modal states
  const [formatModalVisible, setFormatModalVisible] = useState(false);
  const [editingFormat, setEditingFormat] = useState(null);
  const [form] = Form.useForm();

  // Check SuperAdmin privilege (Settings page is SuperAdmin-only)
  const hasAdminPrivilege = Array.isArray(privileges) && privileges.some(p => p.privilege_name === 'SuperAdmin');

  // Debug logging
  useEffect(() => {
    console.log('Settings - Privileges loaded:', privileges);
    console.log('Settings - Has admin privilege:', hasAdminPrivilege);
  }, [privileges, hasAdminPrivilege]);

  useEffect(() => {
    if (hasAdminPrivilege) {
      loadSettings();
      loadOutputFormats();
      loadIndustryPresets();
      loadAuditHistory('all'); // Load all audit history by default
    }
  }, [hasAdminPrivilege]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/aiml/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadOutputFormats = async () => {
    try {
      const response = await api.get('/aiml/output-formats');
      setOutputFormats(response.data);
    } catch (error) {
      console.error('Error loading output formats:', error);
      message.error('Failed to load output formats');
    }
  };

  const loadIndustryPresets = async () => {
    try {
      const response = await api.get('/aiml/industry-presets');
      setIndustryPresets(response.data);
    } catch (error) {
      console.error('Error loading industry presets:', error);
    }
  };

  const loadAuditHistory = async (key) => {
    try {
      setLoading(true);
      setSelectedAuditKey(key);

      if (key === 'all') {
        // Load all audit history
        const response = await api.get('/aiml/settings/history/all');
        setAuditHistory(response.data);
      } else {
        // Load specific setting history
        const response = await api.get(`/aiml/settings/${key}/history`);
        setAuditHistory(response.data);
      }
    } catch (error) {
      console.error('Error loading audit history:', error);
      // Don't show error for 404 (no audit entries) - just set empty array
      if (error.response?.status !== 404) {
        message.error('Failed to load audit history');
      }
      setAuditHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value, industryType = null) => {
    try {
      setLoading(true);
      await api.put(`/aiml/settings/${key}`, {
        value,
        industry_type: industryType
      });
      message.success('Setting updated successfully');
      loadSettings();
    } catch (error) {
      console.error('Error updating setting:', error);
      message.error('Failed to update setting');
    } finally {
      setLoading(false);
    }
  };

  const createOutputFormat = async (values) => {
    try {
      setLoading(true);
      await api.post('/aiml/output-formats', values);
      message.success('Output format created successfully');
      setFormatModalVisible(false);
      form.resetFields();
      loadOutputFormats();
    } catch (error) {
      console.error('Error creating output format:', error);
      message.error('Failed to create output format');
    } finally {
      setLoading(false);
    }
  };

  const updateOutputFormat = async (id, values) => {
    try {
      setLoading(true);
      await api.put(`/aiml/output-formats/${id}`, values);
      message.success('Output format updated successfully');
      setFormatModalVisible(false);
      setEditingFormat(null);
      form.resetFields();
      loadOutputFormats();
    } catch (error) {
      console.error('Error updating output format:', error);
      message.error('Failed to update output format');
    } finally {
      setLoading(false);
    }
  };

  const deleteOutputFormat = async (id) => {
    try {
      setLoading(true);
      await api.delete(`/aiml/output-formats/${id}`);
      message.success('Output format deleted successfully');
      loadOutputFormats();
    } catch (error) {
      console.error('Error deleting output format:', error);
      message.error('Failed to delete output format');
    } finally {
      setLoading(false);
    }
  };

  const resetToDefaults = async (industry) => {
    try {
      setLoading(true);
      await api.post('/aiml/settings/reset-defaults', {
        industry
      });
      message.success(`Settings reset to ${industry} defaults`);
      loadSettings();
    } catch (error) {
      console.error('Error resetting to defaults:', error);
      message.error('Failed to reset settings');
    } finally {
      setLoading(false);
    }
  };

  const handleFormatModalOk = () => {
    form.validateFields().then(values => {
      // Parse column headers if it's a JSON string
      if (values.column_headers && typeof values.column_headers === 'string') {
        try {
          values.column_headers = JSON.parse(values.column_headers);
        } catch (e) {
          message.error('Invalid JSON format for column headers');
          return;
        }
      }

      if (editingFormat) {
        updateOutputFormat(editingFormat.id, values);
      } else {
        createOutputFormat(values);
      }
    });
  };

  const openFormatModal = (format = null) => {
    setEditingFormat(format);
    if (format) {
      form.setFieldsValue({
        format_name: format.format_name,
        format_type: format.format_type,
        column_headers: format.column_headers ? JSON.stringify(format.column_headers, null, 2) : '',
        is_default: format.is_default
      });
    } else {
      form.resetFields();
    }
    setFormatModalVisible(true);
  };

  // Column definitions for output formats table
  const formatColumns = [
    {
      title: 'Format Name',
      dataIndex: 'format_name',
      key: 'format_name',
    },
    {
      title: 'Format Type',
      dataIndex: 'format_type',
      key: 'format_type',
    },
    {
      title: 'Default',
      dataIndex: 'is_default',
      key: 'is_default',
      render: (isDefault) => isDefault ? 'Yes' : 'No',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openFormatModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this format?"
            onConfirm={() => deleteOutputFormat(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Column definitions for audit history table
  const auditColumns = [
    // Setting Key column - only show when "All" is selected
    ...(selectedAuditKey === 'all' ? [{
      title: 'Setting',
      dataIndex: 'setting_key',
      key: 'setting_key',
    }] : []),
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
    },
    {
      title: 'User',
      dataIndex: 'user_name',
      key: 'user_name',
    },
    {
      title: 'Email',
      dataIndex: 'user_email',
      key: 'user_email',
    },
    {
      title: 'Old Value',
      dataIndex: 'old_value',
      key: 'old_value',
      render: (text) => text ? (text.length > 100 ? text.substring(0, 100) + '...' : text) : '-',
    },
    {
      title: 'New Value',
      dataIndex: 'new_value',
      key: 'new_value',
      render: (text) => text ? (text.length > 100 ? text.substring(0, 100) + '...' : text) : '-',
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
    },
  ];

  if (!hasAdminPrivilege) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Access Denied</h2>
          <p>You need AI/ML Settings Admin privilege to access this page.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="settings-container">
      <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
        {/* PROMPTS TAB */}
        <TabPane tab="Prompts" key="prompts">
          <Card title="AI Prompts Configuration" loading={loading}>
            <div style={{ marginBottom: '24px' }}>
              <h3>Test Objectives Prompt</h3>
              <TextArea
                rows={8}
                value={settings.test_objectives_prompt?.value || ''}
                onChange={(e) => {
                  setSettings({
                    ...settings,
                    test_objectives_prompt: { value: e.target.value }
                  });
                }}
                placeholder="Enter the prompt for generating test objectives..."
              />
              <Button
                type="primary"
                style={{ marginTop: '10px' }}
                onClick={() => updateSetting('test_objectives_prompt', settings.test_objectives_prompt?.value)}
              >
                Save Test Objectives Prompt
              </Button>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3>Generate Cases Prompt</h3>
              <TextArea
                rows={8}
                value={settings.generate_cases_prompt?.value || ''}
                onChange={(e) => {
                  setSettings({
                    ...settings,
                    generate_cases_prompt: { value: e.target.value }
                  });
                }}
                placeholder="Enter the prompt for generating test cases..."
              />
              <Button
                type="primary"
                style={{ marginTop: '10px' }}
                onClick={() => updateSetting('generate_cases_prompt', settings.generate_cases_prompt?.value)}
              >
                Save Generate Cases Prompt
              </Button>
            </div>
          </Card>
        </TabPane>

        {/* BUTTON LABELS TAB */}
        <TabPane tab="Button Labels" key="labels">
          <Card title="Customize Button Labels" loading={loading}>
            <div style={{ marginBottom: '24px' }}>
              <h3>Test Objectives Button Label</h3>
              <Input
                value={settings.button_label_objectives?.value || 'Test Objectives'}
                onChange={(e) => {
                  setSettings({
                    ...settings,
                    button_label_objectives: { value: e.target.value }
                  });
                }}
                placeholder="e.g., Test Objectives"
              />
              <Button
                type="primary"
                style={{ marginTop: '10px' }}
                onClick={() => updateSetting('button_label_objectives', settings.button_label_objectives?.value)}
              >
                Save Button Label
              </Button>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3>Generate Cases Button Label</h3>
              <Input
                value={settings.button_label_generate?.value || 'Generate Cases'}
                onChange={(e) => {
                  setSettings({
                    ...settings,
                    button_label_generate: { value: e.target.value }
                  });
                }}
                placeholder="e.g., Generate Cases"
              />
              <Button
                type="primary"
                style={{ marginTop: '10px' }}
                onClick={() => updateSetting('button_label_generate', settings.button_label_generate?.value)}
              >
                Save Button Label
              </Button>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3>File Upload Placeholder</h3>
              <Input
                value={settings.upload_placeholder?.value || 'Upload Specifications/Requirements'}
                onChange={(e) => {
                  setSettings({
                    ...settings,
                    upload_placeholder: { value: e.target.value }
                  });
                }}
                placeholder="e.g., Upload Specifications/Requirements"
              />
              <Button
                type="primary"
                style={{ marginTop: '10px' }}
                onClick={() => updateSetting('upload_placeholder', settings.upload_placeholder?.value)}
              >
                Save Placeholder Text
              </Button>
            </div>
          </Card>
        </TabPane>

        {/* OUTPUT FORMATS TAB */}
        <TabPane tab="Output Formats" key="formats">
          <Card
            title="Output Formats Management"
            loading={loading}
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openFormatModal()}
              >
                Add Format
              </Button>
            }
          >
            <Table
              dataSource={outputFormats}
              columns={formatColumns}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>

          <Modal
            title={editingFormat ? 'Edit Output Format' : 'Create Output Format'}
            visible={formatModalVisible}
            onOk={handleFormatModalOk}
            onCancel={() => {
              setFormatModalVisible(false);
              setEditingFormat(null);
              form.resetFields();
            }}
            width={600}
          >
            <Form form={form} layout="vertical">
              <Form.Item
                name="format_name"
                label="Format Name"
                rules={[{ required: true, message: 'Please enter format name' }]}
              >
                <Input placeholder="e.g., Pharma Test Cases" />
              </Form.Item>

              <Form.Item
                name="format_type"
                label="Format Type"
                rules={[{ required: true, message: 'Please select format type' }]}
              >
                <Select placeholder="Select format type">
                  <Option value="table">Table</Option>
                  <Option value="freeform">Freeform Text</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="column_headers"
                label="Column Headers (JSON)"
                tooltip="Enter column headers as JSON array, e.g., [&quot;Test Case ID&quot;, &quot;Test Case Name&quot;, &quot;Steps&quot;]"
              >
                <TextArea
                  rows={6}
                  placeholder='["Test Case ID", "Test Case Name", "Objective", "Steps", "Expected Result"]'
                />
              </Form.Item>

              <Form.Item
                name="is_default"
                label="Set as Default"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Form>
          </Modal>
        </TabPane>

        {/* INDUSTRY PRESETS TAB */}
        <TabPane tab="Industry Presets" key="presets">
          <Card title="Industry Preset Templates" loading={loading}>
            <p style={{ marginBottom: '20px' }}>
              Quickly configure settings for specific industries. This will reset all prompts and labels to industry-specific defaults.
            </p>

            {Object.keys(industryPresets).map((industry) => (
              <Card
                key={industry}
                type="inner"
                title={industryPresets[industry].name}
                style={{ marginBottom: '16px' }}
                extra={
                  <Popconfirm
                    title={`Reset all settings to ${industryPresets[industry].name} defaults?`}
                    onConfirm={() => resetToDefaults(industry)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="primary">
                      Apply Preset
                    </Button>
                  </Popconfirm>
                }
              >
                <p><strong>Test Objectives Prompt:</strong></p>
                <p style={{ fontSize: '12px', color: '#666' }}>
                  {typeof industryPresets[industry].test_objectives_prompt === 'string'
                    ? industryPresets[industry].test_objectives_prompt.substring(0, 150) + '...'
                    : 'Prompt not available'}
                </p>
                <p><strong>Generate Cases Prompt:</strong></p>
                <p style={{ fontSize: '12px', color: '#666' }}>
                  {typeof industryPresets[industry].generate_cases_prompt === 'string'
                    ? industryPresets[industry].generate_cases_prompt.substring(0, 150) + '...'
                    : 'Prompt not available'}
                </p>
              </Card>
            ))}
          </Card>
        </TabPane>

        {/* AUDIT HISTORY TAB */}
        <TabPane tab="Audit History" key="audit">
          <Card title="Settings Change History" loading={loading}>
            <div style={{ marginBottom: '16px' }}>
              <Select
                value={selectedAuditKey}
                style={{ width: '100%', maxWidth: '400px' }}
                onChange={(key) => loadAuditHistory(key)}
              >
                <Option value="all">All Settings</Option>
                <Option value="test_objectives_prompt">Test Objectives Prompt</Option>
                <Option value="generate_cases_prompt">Generate Cases Prompt</Option>
                <Option value="button_label_objectives">Button Label (Objectives)</Option>
                <Option value="button_label_generate">Button Label (Generate)</Option>
                <Option value="upload_placeholder">Upload Placeholder</Option>
              </Select>
            </div>

            <Table
              dataSource={auditHistory}
              columns={auditColumns}
              rowKey={(record) => `${record.version}-${record.timestamp}`}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'No audit history found' }}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Settings;
